import { useCallback, useEffect, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { UseMutateFunction, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import {
  Column,
  ErrorText,
  LoadingIndicator,
  OptionalLabel,
  Row,
  Text,
} from "@/components";
import { JobFormSection } from "@jobs/job-form/JobFormSection";
import { SaveAndNextButton } from "@jobs/job-form/SaveAndNextButton";
import { JobKeywordsField } from "@/components/companies/jobs/job-form/JobKeywordsField";
import { JobUpdateConfirmationDialog } from "@jobs/job-update-confirmation/JobUpdateConfirmationDialog";
import { FormCurrencyInput } from "@/components/form/FormCurrencyInput";
import { FormEnumMultiselect } from "@/components/form/FormEnumMultiselect";
import { FormEnumSelect } from "@/components/form/FormEnumSelect";
import { FormNumberInput } from "@/components/form/FormNumberInput";
import { FormRichTextInput } from "@/components/form/FormRichTextInput";
import { FormSwitchInput } from "@/components/form/FormSwitchInput";
import { FormTextAreaInput } from "@/components/form/FormTextAreaInput";
import { FormTextInput } from "@/components/form/FormTextInput";
import { FormTimeInput } from "@/components/form/FormTimeInput";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

import { useCurrentCompanyId } from "@/hooks/useCurrentCompanyId";
import { useSuccessToast } from "@/hooks/useSuccessToast";
import { useGetPlacements } from "@/hooks/queries/useGetPlacements";
import { useGetRequisitions } from "@/hooks/queries/useGetRequisitions";
import { useGenerateJobKeywordSuggestions } from "@/hooks/mutations/useGenerateJobKeywordSuggestions";
import { useAddOrSetJobInCompanies } from "@/hooks/sets/useAddOrSetJobInCompanies";
import { useSetJob } from "@/hooks/sets/useSetJob";
import {
  compensationTypeOptions,
  defaultPlacementQueryParams,
  defaultRequisitionQueryParams,
  employmentTypeOptions,
  JOB_FORM_INPUT_CLASS_NAME,
  JOB_FORM_RICH_TEXT_CLASS_NAME,
  JOB_FORM_SELECT_CLASS_NAME,
  jobStatusOptions as allJobStatusOptions,
  jobStatusToLabel,
  MAX_DEFAULT_SEARCH_KEYWORDS,
  MAX_JOB_KEYWORDS,
  positionCategoryOptions,
  revenueTypeOptions,
  shiftTypeOptions,
  shiftTypeToLabel,
  stateOptions,
} from "@/lib/constants";
import { jobs } from "@/lib/jobs";
import { misc } from "@/lib/misc";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { nullableNumberSchema, requiredNumberSchema } from "@/lib/schemas";
import { cn } from "@/lib/utils";
import {
  CompensationType,
  CreateOrUpdateJobRequest,
  EmploymentType,
  Job,
  JobKeyword,
  JobStatus,
  PositionCategory,
  RequisitionStatus,
  RevenueType,
  ShiftType,
} from "@/types";

const hourlyCompensationMax = 1000;
const yearlyCompensationMax = 1000000;

// TODO: Schema improvements:
// - Check enum error messages.
// - Check use of `min(1, ...)`.
// - Check coercion error messages.

const jobSchema = z
  .object({
    address: z.string().min(1, "Required"),
    backfillGuaranteeDays: nullableNumberSchema,
    benefits: z.string(),
    city: z.string().min(1, "Required"),
    compensationMax: requiredNumberSchema,
    compensationMin: requiredNumberSchema,
    compensationType: z.nativeEnum(CompensationType, { message: "Required" }),
    employmentType: z.nativeEnum(EmploymentType, { message: "Required" }),
    estimatedRevenueAmount: nullableNumberSchema,
    isOpenToRelocation: z.boolean(),
    // Keywords are edited as classified objects by JobKeywordsField, so the
    // schema only guards the count.
    keywords: z
      .custom<JobKeyword[]>()
      .refine((value) => value.length > 0, { message: "Required" })
      .refine((value) => value.length <= MAX_JOB_KEYWORDS, {
        message: `Max ${MAX_JOB_KEYWORDS.toString()} keywords`,
      }),
    overview: z.string().min(1, "Required"),
    percentOfSalary: nullableNumberSchema,
    qualifications: z.string().min(1, "Required"),
    positionCategory: z.nativeEnum(PositionCategory, { message: "Required" }),
    responsibilities: z.string().min(1, "Required"),
    revenueType: z.nativeEnum(RevenueType, { message: "Required" }),
    shiftDifferentialMin: nullableNumberSchema,
    shiftDifferentialMax: nullableNumberSchema,
    shiftEnd: z.string(),
    shiftStart: z.string(),
    shiftType: z.array(z.nativeEnum(ShiftType, { message: "Required" })),
    state: z.string().min(1, "Required"),
    status: z.nativeEnum(JobStatus, { message: "Required" }),
    title: z.string().min(1, "Required"),
    topCustomerPriority: z
      .string()
      .trim()
      .min(1, "Required")
      .max(200, "Max 200 characters"),
    zipCode: z.string().min(1, "Required"),
  })
  .superRefine((data, ctx) => {
    const maxLimit =
      data.compensationType === CompensationType.HOURLY
        ? hourlyCompensationMax
        : yearlyCompensationMax;
    const errorMessage =
      data.compensationType === CompensationType.HOURLY
        ? `Hourly compensation should not exceed $${hourlyCompensationMax.toLocaleString()}`
        : `Yearly compensation should not exceed $${yearlyCompensationMax.toLocaleString()}`;

    if (data.compensationMin >= maxLimit) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: errorMessage,
        path: ["compensationMin"],
      });
    }

    if (data.compensationMax >= maxLimit) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: errorMessage,
        path: ["compensationMax"],
      });
    }

    // Validate that maximum compensation is greater than or equal to minimum compensation if both exist.
    if (data.compensationMin > data.compensationMax) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Maximum compensation must be greater than or equal to minimum compensation",
        path: ["compensationMax"],
      });
    }
  });

export type JobSchema = z.infer<typeof jobSchema>;

// Keyword suggestions are generated from the qualifications plus the customer's
// top priority, so the priority's skills influence the suggested keywords too.
const buildKeywordSuggestionsInput = (
  qualifications: string,
  topCustomerPriority: string,
) =>
  topCustomerPriority
    ? `${qualifications}\n\nCustomer's top priority: ${topCustomerPriority}`
    : qualifications;

export const JobForm = ({
  className,
  job,
  mutationFn,
  isPending,
  isError,
  submitButtonIcon,
  submitButtonText,
}: {
  className?: string;
  job?: Job;
  mutationFn: UseMutateFunction<
    Job,
    Error,
    {
      request: CreateOrUpdateJobRequest;
    }
  >;
  isPending: boolean;
  isError: boolean;
  submitButtonIcon: React.ReactElement;
  submitButtonText: string;
}) => {
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const hasRequestedKeywordSuggestions = useRef(false);
  const keywordSuggestionsRequestId = useRef(0);

  const form = useForm<JobSchema>({
    defaultValues: {
      address: job?.address ?? "",
      backfillGuaranteeDays: job?.backfillGuaranteeDays ?? null,
      benefits: job?.benefits ?? "",
      city: job?.city ?? "",
      // Need to use `undefined` as the empty value so that zod interprets the value as missing if not specified.
      compensationMax: job?.compensationMax,
      // Need to use `undefined` as the empty value so that zod interprets the value as missing if not specified.
      compensationMin: job?.compensationMin,
      compensationType: job?.compensationType ?? CompensationType.HOURLY,
      employmentType: job?.employmentType ?? EmploymentType.FULLTIME,
      estimatedRevenueAmount: job?.estimatedRevenueAmount ?? null,
      isOpenToRelocation: job?.isOpenToRelocation ?? false,
      keywords: job?.keywords ?? [],
      overview: job?.overview ?? "",
      percentOfSalary: job?.percentOfSalary ?? null,
      positionCategory: job?.positionCategory ?? undefined,
      qualifications: job?.qualifications ?? "",
      responsibilities: job?.responsibilities ?? "",
      revenueType: job?.revenueType ?? RevenueType.FIXED,
      shiftDifferentialMin: job?.shiftDifferentialMin,
      shiftDifferentialMax: job?.shiftDifferentialMax,
      shiftEnd: job?.shiftEnd ?? "",
      shiftStart: job?.shiftStart ?? "",
      shiftType: job?.shiftType ?? [ShiftType.FIRSTSHIFT],
      state: job?.state ?? "",
      status: job?.status ?? JobStatus.DRAFT,
      title: job?.title ?? "",
      topCustomerPriority: job?.topCustomerPriority ?? "",
      zipCode: job?.zipCode ?? "",
    },
    resolver: zodResolver(jobSchema),
  });
  const currentCompanyId = useCurrentCompanyId();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const successToast = useSuccessToast();
  const setJob = useSetJob();
  const addOrSetJobInCompanies = useAddOrSetJobInCompanies();
  const {
    isPending: isKeywordSuggestionsPending,
    mutate: generateJobKeywordSuggestions,
  } = useGenerateJobKeywordSuggestions();

  const {
    data: requisitions,
    isPending: isRequisitionsPending,
    isError: isRequisitionsError,
  } = useGetRequisitions({
    jobId: job?.id,
    queryOptions: defaultRequisitionQueryParams,
  });

  const { data: placements } = useGetPlacements({
    jobId: job?.id,
    queryOptions: defaultPlacementQueryParams,
  });

  const compensationType = form.watch("compensationType");
  const compensationMin = form.watch("compensationMin");
  const compensationMax = form.watch("compensationMax");
  const isOpenToRelocation = form.watch("isOpenToRelocation");
  const keywords = form.watch("keywords");
  const percentOfSalary = form.watch("percentOfSalary");
  const qualifications = form.watch("qualifications");
  const topCustomerPriority = form.watch("topCustomerPriority");
  const revenueType = form.watch("revenueType");
  const compensationPlaceholder =
    compensationType === CompensationType.HOURLY ? "$0 /hr" : "$0 /yr";
  const hasKeywords = keywords.length > 0;

  // Generating replaces the list with the generated set; the first
  // MAX_DEFAULT_SEARCH_KEYWORDS returned become the search defaults.
  const applyGeneratedKeywords = useCallback(
    (generatedKeywords: JobKeyword[]) => {
      const nextKeywords = generatedKeywords
        .slice(0, MAX_JOB_KEYWORDS)
        .map((keyword, index) => ({
          ...keyword,
          isDefaultSearchKeyword: index < MAX_DEFAULT_SEARCH_KEYWORDS,
        }));

      form.setValue("keywords", nextKeywords, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [form],
  );

  // Existing jobs saved before keywords existed get suggestions on open,
  // since the user never blurs qualifications to trigger them.
  useEffect(() => {
    if (
      !job ||
      job.keywords.length ||
      !job.qualifications ||
      hasRequestedKeywordSuggestions.current
    ) {
      return;
    }
    hasRequestedKeywordSuggestions.current = true;
    // Superseded requests (StrictMode refire, job cache updates) must not
    // apply their late responses over newer form state.
    const requestId = ++keywordSuggestionsRequestId.current;
    generateJobKeywordSuggestions(
      {
        qualifications: buildKeywordSuggestionsInput(
          job.qualifications,
          job.topCustomerPriority ?? "",
        ),
      },
      {
        onSuccess: ({ keywords: generatedKeywords }) => {
          if (requestId !== keywordSuggestionsRequestId.current) return;
          applyGeneratedKeywords(generatedKeywords);
        },
      },
    );
    // StrictMode detaches the mutation observer on the remount cycle, which
    // strands the request pending forever; reset the guard so the remount
    // re-fires and re-attaches.
    return () => {
      hasRequestedKeywordSuggestions.current = false;
    };
  }, [applyGeneratedKeywords, generateJobKeywordSuggestions, job]);

  const handleSubmit = (data: JobSchema) => {
    if (!job) {
      const isGoingActive = data.status === JobStatus.ACTIVE;
      if (isGoingActive) {
        setIsUpdateDialogOpen(true);
        return;
      }
      submitJob(data);
      return;
    }

    // The following checks are used to determine if the user should be
    // prompted to review the consequences of certain changes before submitting.
    const isGoingActive =
      data.status === JobStatus.ACTIVE && job.status !== JobStatus.ACTIVE;
    const hasShiftChanges =
      [...data.shiftType].sort().join(",") !==
        [...job.shiftType].sort().join(",") ||
      data.shiftDifferentialMin !== job.shiftDifferentialMin ||
      data.shiftDifferentialMax !== job.shiftDifferentialMax;
    const hasCompChanges =
      data.compensationMin !== job.compensationMin ||
      data.compensationMax !== job.compensationMax;
    const hasRevenueChanges =
      data.backfillGuaranteeDays !== job.backfillGuaranteeDays ||
      data.estimatedRevenueAmount !== job.estimatedRevenueAmount;

    const hasAffectedRequisitions =
      hasShiftChanges &&
      (requisitions ?? []).some((r) =>
        [
          RequisitionStatus.LIVE,
          RequisitionStatus.PENDING,
          RequisitionStatus.BACKFILL,
        ].includes(r.status),
      );
    const hasAffectedPlacements =
      (hasCompChanges || hasRevenueChanges) && (placements ?? []).length > 0;

    if (isGoingActive || hasAffectedRequisitions || hasAffectedPlacements) {
      setIsUpdateDialogOpen(true);
      return;
    }

    submitJob(data);
  };

  const handleKeywordsGenerate = () => {
    // Both inputs feed generation; the blur trigger can fire before the
    // customer priority is filled in, so guard here too.
    if (!qualifications || !topCustomerPriority) {
      return;
    }

    generateJobKeywordSuggestions(
      {
        qualifications: buildKeywordSuggestionsInput(
          qualifications,
          topCustomerPriority,
        ),
      },
      {
        onSuccess: ({ keywords: generatedKeywords }) => {
          applyGeneratedKeywords(generatedKeywords);
        },
      },
    );
  };

  const submitJob = (data: JobSchema) => {
    const { keywords, ...request } = data;
    mixpanelUtils.trackFormSubmission(
      "Job create/update",
      MIXPANEL_SCREENS.JOB,
      {
        has_title: !!data.title,
        has_overview: !!data.overview,
        has_responsibilities: !!data.responsibilities,
        has_qualifications: !!data.qualifications,
        has_benefits: !!data.benefits,
        has_shiftType: data.shiftType.length > 0,
        status: data.status,
        employmentType: data.employmentType,
      },
    );
    mutationFn(
      {
        request: {
          ...request,
          // ! TODO: Find a more robust way to handle nullable fields.
          benefits: data.benefits || null,
          keywords,
          shiftStart: data.shiftStart || null,
          shiftEnd: data.shiftEnd || null,
        },
      },
      {
        onSuccess: (updatedJob) => {
          // Invalidate requisitions query because if shiftType, shiftDiffMin,
          // or shiftDiffMax changes, requisitions will be updated on BE
          void queryClient.invalidateQueries({
            queryKey: [
              "requisitions",
              updatedJob.id,
              defaultRequisitionQueryParams,
            ],
          });

          const hasShiftChanges = job
            ? [...data.shiftType].sort().join(",") !==
                [...job.shiftType].sort().join(",") ||
              data.shiftDifferentialMin !== job.shiftDifferentialMin ||
              data.shiftDifferentialMax !== job.shiftDifferentialMax
            : false;

          const affectedRequisitionCount = (requisitions ?? []).filter((r) =>
            [
              RequisitionStatus.LIVE,
              RequisitionStatus.PENDING,
              RequisitionStatus.BACKFILL,
            ].includes(r.status),
          ).length;

          const showReqsUpdated =
            hasShiftChanges && affectedRequisitionCount > 0;

          successToast({
            description: showReqsUpdated
              ? `Job updated successfully and ${affectedRequisitionCount.toString()} requisition${affectedRequisitionCount === 1 ? "" : "s"} had their shift details updated automatically.`
              : "Job updated successfully.",
          });

          addOrSetJobInCompanies({ job: updatedJob });

          setJob({ job: updatedJob });

          // TEMPORARY: External sourcing tickets are now auto-created on a
          // job's first ACTIVE transition, so stop funneling newly created
          // jobs into the manual external-sourcing form. Restore the branch
          // below when deemed appropriate.
          void navigate(
            `/companies/${currentCompanyId}/jobs/${updatedJob.id}?t=overview`,
            // job
            //   ? `/companies/${currentCompanyId}/jobs/${updatedJob.id}?t=overview`
            //   : `/companies/${currentCompanyId}/jobs/${updatedJob.id}/external-sourcing`,
          );
        },
      },
    );
  };

  const handleEstimatedRevenueChangingField = (
    value: number | string,
    fieldName:
      | "compensationMin"
      | "compensationMax"
      | "compensationType"
      | "percentOfSalary",
  ) => {
    if (revenueType === RevenueType.PERCENT) {
      let revenue: number | string;
      if (fieldName === "compensationMin") {
        revenue = misc.calculateEstimatedRevenueAmount(
          compensationType === CompensationType.HOURLY,
          Number(value),
          Number(compensationMax),
          Number(percentOfSalary),
        );
      } else if (fieldName === "compensationMax") {
        revenue = misc.calculateEstimatedRevenueAmount(
          compensationType === CompensationType.HOURLY,
          Number(compensationMin),
          Number(value),
          Number(percentOfSalary),
        );
      } else if (fieldName === "percentOfSalary") {
        revenue = misc.calculateEstimatedRevenueAmount(
          compensationType === CompensationType.HOURLY,
          Number(compensationMin),
          Number(compensationMax),
          Number(value),
        );
      } else {
        revenue = misc.calculateEstimatedRevenueAmount(
          value === CompensationType.HOURLY,
          Number(compensationMin),
          Number(compensationMax),
          Number(percentOfSalary),
        );
      }
      form.setValue("estimatedRevenueAmount", revenue as number);
    }
  };

  const renderForm = () => {
    return (
      <Form {...form}>
        <form
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <Column className={cn("max-w-5xl gap-16", className)}>
            {/* Job details */}
            <JobFormSection
              title="1. Job Details"
              description="Provide details about the job."
            >
              {/* Job title */}
              <FormTextInput
                fieldName="title"
                form={form}
                inputClassName={JOB_FORM_INPUT_CLASS_NAME}
                label="Job title"
                placeholder="Job title"
              />
              {/* Position category */}
              <FormEnumSelect
                fieldName="positionCategory"
                form={form}
                infoTooltip="Please ensure that the category is correct. This has downstream consequences for various worker functionalities."
                inputClassName={JOB_FORM_SELECT_CLASS_NAME}
                label="Position category"
                options={positionCategoryOptions}
                placeholder="Position category"
              />
              {/* Address */}
              <FormTextInput
                fieldName="address"
                form={form}
                inputClassName={JOB_FORM_INPUT_CLASS_NAME}
                label="Address"
                placeholder="Address"
              />
              <Row className="gap-4">
                {/* City */}
                <FormTextInput
                  className="w-full"
                  fieldName="city"
                  form={form}
                  inputClassName={JOB_FORM_INPUT_CLASS_NAME}
                  label="City"
                  placeholder="City"
                />
                {/* State */}
                <FormEnumSelect
                  className="w-full"
                  fieldName="state"
                  form={form}
                  inputClassName={JOB_FORM_SELECT_CLASS_NAME}
                  label="State"
                  options={stateOptions}
                  placeholder="State"
                />
                {/* Zip code */}
                <FormNumberInput
                  className="w-full"
                  fieldName="zipCode"
                  form={form}
                  inputClassName={JOB_FORM_INPUT_CLASS_NAME}
                  label="Zip code"
                  maxLength={5}
                  placeholder="Zip code"
                />
              </Row>
            </JobFormSection>

            {/* Compensation */}
            <JobFormSection
              title="2. Compensation"
              description="Include pay details and benefit or perks your company offers."
            >
              <Row className="gap-4">
                {/* Employment type */}
                <FormEnumSelect
                  className="w-full"
                  fieldName="employmentType"
                  form={form}
                  inputClassName={JOB_FORM_SELECT_CLASS_NAME}
                  label="Employment type"
                  options={employmentTypeOptions}
                  placeholder="Employment type"
                />
                {/* Shift type */}
                <FormEnumMultiselect
                  enumToLabel={shiftTypeToLabel}
                  fieldName="shiftType"
                  form={form}
                  inputClassName={JOB_FORM_SELECT_CLASS_NAME}
                  label="Shift type"
                  options={shiftTypeOptions}
                />
              </Row>
              <Row className="gap-4">
                {/* Shift start time */}
                <FormTimeInput
                  className="w-full"
                  fieldName="shiftStart"
                  form={form}
                  inputClassName={JOB_FORM_INPUT_CLASS_NAME}
                  label="Shift start time"
                  optional
                />
                {/* Shift end time */}
                <FormTimeInput
                  className="w-full"
                  fieldName="shiftEnd"
                  form={form}
                  inputClassName={JOB_FORM_INPUT_CLASS_NAME}
                  label="Shift end time"
                  optional
                />
              </Row>
              <Row className="gap-4">
                {/* Compensation type */}
                <FormEnumSelect
                  className="w-full"
                  fieldName="compensationType"
                  form={form}
                  inputClassName={JOB_FORM_SELECT_CLASS_NAME}
                  label="Compensation type"
                  onChange={(value) => {
                    handleEstimatedRevenueChangingField(
                      value,
                      "compensationType",
                    );
                  }}
                  options={compensationTypeOptions}
                  placeholder="Compensation type"
                />
                {/* Minimum */}
                <FormCurrencyInput
                  className="w-full"
                  fieldName="compensationMin"
                  form={form}
                  inputClassName={JOB_FORM_INPUT_CLASS_NAME}
                  label="Minimum Compensation"
                  onChange={(value) => {
                    handleEstimatedRevenueChangingField(
                      value,
                      "compensationMin",
                    );
                  }}
                  placeholder={compensationPlaceholder}
                />
                {/* Maximum */}
                <FormCurrencyInput
                  className="w-full"
                  fieldName="compensationMax"
                  form={form}
                  inputClassName={JOB_FORM_INPUT_CLASS_NAME}
                  label="Maximum Compensation"
                  onChange={(value) => {
                    handleEstimatedRevenueChangingField(
                      value,
                      "compensationMax",
                    );
                  }}
                  placeholder={compensationPlaceholder}
                />
              </Row>
              {/* Shift differential */}
              <Row className="gap-4">
                <FormCurrencyInput
                  className="w-full"
                  disabled={compensationType === CompensationType.YEARLY}
                  fieldName="shiftDifferentialMin"
                  form={form}
                  inputClassName={JOB_FORM_INPUT_CLASS_NAME}
                  label="Shift differential min"
                  optional
                  placeholder={
                    compensationType === CompensationType.HOURLY
                      ? "$0 /hr"
                      : "N/A"
                  }
                />
                <FormCurrencyInput
                  className="w-full"
                  disabled={compensationType === CompensationType.YEARLY}
                  fieldName="shiftDifferentialMax"
                  form={form}
                  inputClassName={JOB_FORM_INPUT_CLASS_NAME}
                  label="Shift differential max"
                  optional
                  placeholder={
                    compensationType === CompensationType.HOURLY
                      ? "$0 /hr"
                      : "N/A"
                  }
                />
              </Row>
              {/* Benefits */}
              <FormRichTextInput
                fieldName="benefits"
                form={form}
                inputClassName={JOB_FORM_RICH_TEXT_CLASS_NAME}
                label="Benefits"
                optional
              />
            </JobFormSection>

            {/* Job description */}
            <JobFormSection
              title="3. Job Description"
              description="Include as much detail as possible about the role, responsibilities and required qualifications."
            >
              {/* Job summary */}
              <FormRichTextInput
                fieldName="overview"
                form={form}
                inputClassName={JOB_FORM_RICH_TEXT_CLASS_NAME}
                label="Job summary"
              />
              {/* Responsibilities */}
              <FormRichTextInput
                fieldName="responsibilities"
                form={form}
                inputClassName={JOB_FORM_RICH_TEXT_CLASS_NAME}
                label="Responsibilities"
              />
              {/* Qualifications */}
              <FormRichTextInput
                fieldName="qualifications"
                form={form}
                inputClassName={JOB_FORM_RICH_TEXT_CLASS_NAME}
                label="Qualifications"
                onBlur={() => {
                  if (hasKeywords) return;
                  handleKeywordsGenerate();
                }}
                sublabel="Drives search results and job recommendations — include every important skill."
              />
              {/* Open to relocation */}
              <FormSwitchInput
                fieldName="isOpenToRelocation"
                form={form}
                label="Open to relocation"
              />
            </JobFormSection>
            {/* If flipping job to open to relocation, show warning */}
            {!job?.isOpenToRelocation && isOpenToRelocation && (
              <p className="rounded-md bg-amber-50 px-4 py-3 text-sm font-medium text-amber-700">
                Warning: Enabling this will make the job eligible for workers
                who are open to relocating. These candidates may be
                automatically surfaced in the sourcing portal when they express
                job-seeking intent, regardless of their current location.
              </p>
            )}

            {/* Status */}
            {job && (
              <JobFormSection
                title="4. Job Status"
                description="Set the status of the job."
              >
                {/* Job Status */}
                <FormEnumSelect
                  // Don't allow jobs to be reopened, as it will make for a confusing user experience on the worker app
                  disabled={form.watch("status") === JobStatus.CLOSED}
                  fieldName="status"
                  form={form}
                  inputClassName={JOB_FORM_SELECT_CLASS_NAME}
                  label="Job status"
                  options={
                    form.watch("status") === JobStatus.ACTIVE
                      ? [
                          {
                            label: jobStatusToLabel[JobStatus.ACTIVE],
                            value: JobStatus.ACTIVE,
                          },
                          {
                            label: jobStatusToLabel[JobStatus.CLOSED],
                            value: JobStatus.CLOSED,
                          },
                        ]
                      : allJobStatusOptions.map((option) =>
                          option.value === JobStatus.ACTIVE
                            ? {
                                ...option,
                                disabledReason:
                                  jobs.activationBlockedReason(job),
                              }
                            : option,
                        )
                  }
                  placeholder="Job status"
                />
              </JobFormSection>
            )}

            {/* Prefill information */}
            <JobFormSection
              title={
                !job
                  ? "4. Requisition and Placement Prefills"
                  : "5. Requisition and Placement Prefills"
              }
              description="Include information that will help prefill information on requisitions and placements related to followups and revenue."
            >
              <Row className="gap-4">
                <FormNumberInput
                  className="w-full"
                  fieldName="backfillGuaranteeDays"
                  form={form}
                  inputClassName={JOB_FORM_INPUT_CLASS_NAME}
                  label="Backfill guarantee days"
                  optional
                  placeholder="Enter integer..."
                />
                <FormEnumSelect
                  className="w-full"
                  fieldName="revenueType"
                  form={form}
                  inputClassName={JOB_FORM_SELECT_CLASS_NAME}
                  label="Revenue type"
                  onChange={() => {
                    form.setValue("estimatedRevenueAmount", null);
                    form.setValue("percentOfSalary", null);
                  }}
                  optional
                  options={revenueTypeOptions}
                  placeholder="Revenue type"
                />
              </Row>
              <Row className="gap-4">
                <FormField
                  control={form.control}
                  name="percentOfSalary"
                  render={({ field }) => {
                    const isDisabled =
                      form.watch("revenueType") !== RevenueType.PERCENT;
                    return (
                      <FormItem className="w-full text-slate-700">
                        <FormLabel>
                          Percent of{" "}
                          {compensationType === CompensationType.HOURLY
                            ? "annualized"
                            : ""}{" "}
                          salary
                          <OptionalLabel className="ml-1.5" />
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            className={JOB_FORM_INPUT_CLASS_NAME}
                            disabled={isDisabled}
                            onChange={(e) => {
                              field.onChange(e.target.value);
                              handleEstimatedRevenueChangingField(
                                e.target.value,
                                "percentOfSalary",
                              );
                            }}
                            onKeyDown={misc.enforceCurrencyInput}
                            placeholder="Enter the percent..."
                            value={(field.value ?? "") as string}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
                <FormCurrencyInput
                  className="w-full"
                  disabled={form.watch("revenueType") === RevenueType.PERCENT}
                  fieldName="estimatedRevenueAmount"
                  form={form}
                  inputClassName={JOB_FORM_INPUT_CLASS_NAME}
                  label="Estimated revenue amount"
                  optional
                  placeholder="Enter dollar amount..."
                />
              </Row>
            </JobFormSection>

            {/* Customer priority */}
            <JobFormSection
              title={!job ? "5. Customer Priority" : "6. Customer Priority"}
              description="Capture the single thing this customer cares about most for this hire. This is emphasized at the start of AI candidate summaries."
            >
              <FormTextAreaInput
                fieldName="topCustomerPriority"
                form={form}
                inputClassName={JOB_FORM_INPUT_CLASS_NAME}
                label="Customer's top priority"
                maxLength={200}
                placeholder="e.g. Reliability and on-time attendance matter more than raw years of experience."
              />
              <FormField
                control={form.control}
                name="keywords"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Keywords</FormLabel>
                    <Text className="text-sm font-normal text-slate-500">
                      Generated from the job qualifications & customer top
                      priority. Every keyword highlights matches on a candidate
                      profile; the ones toggled (up to{" "}
                      {MAX_DEFAULT_SEARCH_KEYWORDS.toString()}) will be default
                      keyword filters on Search. Up to{" "}
                      {MAX_JOB_KEYWORDS.toString()} keywords.
                    </Text>
                    <JobKeywordsField
                      isGenerating={isKeywordSuggestionsPending}
                      keywords={field.value}
                      onChange={(nextKeywords) => {
                        field.onChange(nextKeywords);
                      }}
                      onGenerate={
                        qualifications && topCustomerPriority
                          ? handleKeywordsGenerate
                          : undefined
                      }
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </JobFormSection>

            <Column className="gap-4">
              <Row className="justify-end">
                {job ? (
                  <Row className="items-center gap-3">
                    <Button type="submit" disabled={isPending || isError}>
                      {isPending ? (
                        <LoadingIndicator gearColor="white" variant="small" />
                      ) : (
                        <>
                          {submitButtonIcon}
                          {submitButtonText}
                        </>
                      )}
                    </Button>
                  </Row>
                ) : (
                  <SaveAndNextButton disabled={isError} isPending={isPending} />
                )}
              </Row>
              {isError && (
                <Column className="items-end">
                  <ErrorText errorMessage="An error occurred while saving the job. Please let the engineers know if they are not already on it!" />
                </Column>
              )}
            </Column>
          </Column>
        </form>
      </Form>
    );
  };

  const render = () => {
    // If job doesn't exist (for create job form), isRequisitionsPending will remain true
    if (isRequisitionsPending && job?.id) {
      return <LoadingIndicator variant="medium" />;
    } else if (isRequisitionsError) {
      return (
        <Column className="h-screen items-center">
          <ErrorText errorMessage="An error occurred while finding the requisition. Please let the engineers know if they are not already on it!" />
        </Column>
      );
    }
    return (
      <>
        {renderForm()}
        <JobUpdateConfirmationDialog
          companyId={currentCompanyId}
          formValues={form.getValues()}
          isError={isError}
          isLoading={isPending}
          onConfirm={() => {
            submitJob(form.getValues());
            setIsUpdateDialogOpen(false);
          }}
          onOpenChange={setIsUpdateDialogOpen}
          open={isUpdateDialogOpen}
          originalJob={job}
          placements={placements ?? []}
          requisitions={requisitions ?? []}
        />
      </>
    );
  };
  return render();
};
